import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Wrench, BellRing, Loader2, Save, CheckCircle2, Users, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Ajustes del módulo Taller: umbral (días) para el aviso de vencimiento de
 * mantenimiento preventivo. Se usa tanto en el panel visual del Dashboard de
 * Taller como en el email semanal automático a los managers del Taller.
 */
export default function WorkshopSettingsTab() {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [threshold, setThreshold] = useState(90);
  const [mechanics, setMechanics] = useState([]);
  const [newMechanic, setNewMechanic] = useState("");
  const [addingMechanic, setAddingMechanic] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get("/workshop/maintenance-settings");
      setThreshold(data.threshold_days);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  const refreshMechanics = async () => {
    try {
      const { data } = await authAxios.get("/workshop/mechanics");
      setMechanics(data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { refresh(); refreshMechanics(); }, []);

  const save = async () => {
    const value = parseInt(threshold, 10);
    if (!value || value < 1) {
      toast.error("Introduce un número de días válido");
      return;
    }
    setSaving(true);
    try {
      await authAxios.patch("/admin/workshop/maintenance-settings", { threshold_days: value });
      toast.success("Umbral de vencimiento actualizado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar el ajuste");
    } finally { setSaving(false); }
  };

  const addMechanic = async () => {
    const name = newMechanic.trim();
    if (!name) return;
    setAddingMechanic(true);
    try {
      await authAxios.post("/admin/workshop/mechanics", { name });
      setNewMechanic("");
      toast.success("Mecánico añadido");
      refreshMechanics();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo añadir");
    } finally { setAddingMechanic(false); }
  };

  const deleteMechanic = async (id) => {
    try {
      await authAxios.delete(`/admin/workshop/mechanics/${id}`);
      setMechanics(prev => prev.filter(m => m.id !== id));
    } catch (e) { toast.error("No se pudo eliminar"); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="workshop-settings-tab">
      <div>
        <h2 className="font-bold text-slate-800 text-base flex items-center gap-2">
          <Wrench size={18} className="text-[#EA580C]" /> Taller — Parque Central de Maquinaria
        </h2>
        <p className="text-slate-500 text-sm mt-1">
          Configura cada cuántos días sin mantenimiento preventivo se considera que una máquina está pendiente de revisión.
        </p>
      </div>

      <div className="card-white p-6 max-w-lg">
        <h3 className="text-sm font-bold text-slate-700 mb-1 flex items-center gap-2">
          <BellRing size={16} className="text-amber-600" /> Umbral de aviso por vencimiento
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          Máquinas sin preventivo desde hace más de este número de días aparecerán en el panel "Aviso por vencimiento" del Dashboard de Taller y en el email semanal automático a los managers del Taller.
        </p>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <Loader2 size={16} className="animate-spin" /> Cargando...
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <input
              data-testid="workshop-threshold-input"
              type="number"
              min="1"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              className="w-28 px-3 py-2 border border-slate-300 rounded-lg text-sm font-semibold text-center"
            />
            <span className="text-sm text-slate-500">días</span>
            <button
              data-testid="workshop-threshold-save"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#EA580C] hover:bg-[#C2410C] text-white font-semibold rounded-lg text-sm disabled:opacity-50 ml-2"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Guardar
            </button>
          </div>
        )}
        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-400">
          <CheckCircle2 size={13} className="text-emerald-500" />
          El email semanal se envía automáticamente todos los lunes a las 7:00 a los usuarios con acceso al Taller.
        </div>
      </div>

      <div className="card-white p-6 max-w-lg">
        <h3 className="text-sm font-bold text-slate-700 mb-1 flex items-center gap-2">
          <Users size={16} className="text-[#EA580C]" /> Listado de mecánicos
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          Aparecen como sugerencia en el campo "Mecánico" del parte. Se añaden solos cuando la IA lee un nombre nuevo del manuscrito o cuando alguien escribe uno no listado; aquí puedes añadirlos o quitarlos a mano.
        </p>
        <div className="flex items-center gap-2 mb-4">
          <input
            data-testid="workshop-mechanic-input"
            type="text"
            value={newMechanic}
            onChange={(e) => setNewMechanic(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addMechanic()}
            placeholder="Nombre del mecánico"
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
          />
          <button
            data-testid="workshop-mechanic-add"
            onClick={addMechanic}
            disabled={addingMechanic || !newMechanic.trim()}
            className="inline-flex items-center gap-1 px-3 py-2 bg-[#EA580C] hover:bg-[#C2410C] text-white font-semibold rounded-lg text-sm disabled:opacity-50"
          >
            {addingMechanic ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Añadir
          </button>
        </div>
        {mechanics.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-3">Sin mecánicos en el listado todavía.</p>
        ) : (
          <ul className="divide-y divide-slate-100 max-h-64 overflow-y-auto" data-testid="workshop-mechanics-list">
            {mechanics.map(m => (
              <li key={m.id} data-testid={`workshop-mechanic-row-${m.id}`} className="flex items-center justify-between py-2 text-sm">
                <span className="text-slate-700">{m.name}</span>
                <button data-testid={`workshop-mechanic-delete-${m.id}`} onClick={() => deleteMechanic(m.id)}
                  className="p-1 rounded hover:bg-red-50 text-red-500">
                  <Trash2 size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
